import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { convertStringToStringDouble } from "../../../../../utils/ConverterUtils";
import { toLocalISOString } from "../../../../../utils/DateUtils";
import { useBranch } from "../../../../../context/BranchContext";
import { ApiError } from "../../../Common/ApiError";
import { successAlert, warnAlert } from "../../../alert/defaultAlert";
import AsteriskRequired from "../../../Common/AsteriskRequired";
import InputMoney from "../../../Forms/Element/InputMoney";
import InputText from "../../../Forms/Element/InputText";
import ModalPageEvent from "../../../Common/Modal/ModalPageEvent";
import BranchSelect from "../../Main/Branch/BranchSelect";
import PaymentMethod from "../../Common/PaymentMethod/PaymentMethod";
import TransactionCashFlowType from "../Transaction/TransactionCashFlowType";
import TransactionCashFlowTypeSelect from "../Transaction/TransactionCashFlowTypeSelect";

/**
 * Lançamento rápido de movimentação a partir da tela de Caixas.
 *
 * A recepção costuma ter acesso só a esta tela, e é ela quem tira dinheiro da gaveta para
 * uma compra do dia (o café, a água do fornecedor). Sem este atalho, a saída ou ficava sem
 * registro — e o caixa não fechava no fim do turno — ou dependia de alguém com acesso à tela
 * de Movimentações lançar depois, de memória.
 *
 * O caixa não é escolhido aqui: o backend resolve o caixa aberto do usuário logado na filial
 * (TransactionCashFlowService.save). É de propósito. O dinheiro saiu da gaveta de quem está
 * lançando, então a movimentação pertence ao caixa dessa pessoa; poder escolher o caixa de
 * outro operador só serviria para estourar o fechamento alheio.
 */
const CashTransactionModal = ({ show, setShow, onSaved }) => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();

    // Saída é o caso que traz o operador até aqui. Entrada existe (um troco devolvido, um
    // aporte), mas é a exceção, então não obriga a escolher o óbvio toda vez.
    const defaultType = useMemo(
        () => ({ value: TransactionCashFlowType.OUT, label: t('out') }),
        [t]
    );

    const [type, setType] = useState(defaultType);
    const [branch, setBranch] = useState(null);
    const [value, setValue] = useState(0);
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Com filial no contexto o campo fica preenchido e travado, como no restante do sistema:
    // quem está operando uma unidade não deveria conseguir lançar na gaveta de outra.
    const isBranchFixed = Boolean(selectedBranch);

    const resetFields = useCallback(() => {
        setType(defaultType);
        setValue(0);
        setDescription('');
        setBranch(selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null);
    }, [defaultType, selectedBranch]);

    // Reabrir o modal precisa começar limpo: o operador que lançou o café não quer encontrar
    // o valor e a descrição anteriores no próximo lançamento.
    useEffect(() => {
        if (show) {
            resetFields();
        }
    }, [show, resetFields]);

    const handleClose = () => {
        setShow(false);
    };

    const handleSave = () => {
        if (!branch?.value) {
            warnAlert(t('validation.required.branch'));
            return;
        }

        if (!type?.value) {
            warnAlert(t('validation.required.type'));
            return;
        }

        const numericValue = Number(convertStringToStringDouble(value));
        if (!numericValue || numericValue <= 0) {
            warnAlert(t('validation.required.value'));
            return;
        }

        if (!description.trim()) {
            warnAlert(t('validation.required.description'));
            return;
        }

        setIsSubmitting(true);

        const payload = {
            value: convertStringToStringDouble(value),
            branch: { id: branch.value },
            description: description.trim(),
            type: type.value,
            // Sempre dinheiro: o que sai desta tela é a cédula da gaveta. Compra no cartão da
            // empresa não passa pelo caixa e continua sendo contas a pagar.
            paymentMethod: PaymentMethod.CASH,
            // ISO local (sem 'Z'): a movimentação é do fuso da barbearia, e converter para UTC
            // deslocaria o lançamento de dia perto da meia-noite.
            transactionDate: toLocalISOString(new Date())
        };

        axiosInstance.post(Api.cash_flow.transaction, payload)
            .then(() => {
                setShow(false);
                successAlert(t('message.operation_successfully'), () => {
                    if (onSaved) {
                        onSaved();
                    }
                });
            })
            // Sem caixa aberto na filial, o backend recusa com a mensagem dele mesmo, que já
            // nomeia o usuário e a filial. Repetir a regra aqui só criaria duas versões da
            // mesma explicação para manter em sincronia.
            .catch(error => ApiError(error))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <ModalPageEvent
            show={show}
            setShow={setShow}
            centered={true}
            title={t('cash_flow.new_transaction')}
            btnText={t('save')}
            btnOnClick={handleSave}
            btnDisabled={isSubmitting}
            btnCloseOnClick={handleClose}
            child={
                <Row className="g-3">
                    <Col md={12}>
                        <FormGroup>
                            <FormLabel>{t('branch')} <AsteriskRequired /></FormLabel>
                            <BranchSelect
                                value={branch}
                                onChange={(item) => setBranch(item)}
                                isDisabled={isBranchFixed}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('type')} <AsteriskRequired /></FormLabel>
                            <TransactionCashFlowTypeSelect
                                value={type}
                                onChange={(item) => setType(item)}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <FormLabel>{t('value')} ({t('cash')}) <AsteriskRequired /></FormLabel>
                            <InputMoney
                                name="value"
                                value={value}
                                // InputMoney entrega o valor normalizado, preservando a parte
                                // decimal para que lançamentos como R$ 10,50 não virem R$ 1050.
                                onChange={(nextValue) => setValue(nextValue)}
                                style={{ fontWeight: 'bold', fontSize: '1.05rem' }}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup>
                            <FormLabel>{t('description')} <AsteriskRequired /></FormLabel>
                            <InputText
                                name="description"
                                maxLength={50}
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                                placeholder={t('cash_flow.transaction_description_placeholder')}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <small className="text-muted d-block">
                            O lançamento entra no seu caixa aberto da filial, na data e hora de agora.
                        </small>
                    </Col>
                </Row>
            }
        />
    );
};

export default CashTransactionModal;
